import { EventType, getEventTypeLabel } from '@/features/events/types/EventType.ts';
import { Sidebar } from '@/common/components/sidebar/Sidebar.tsx';
import { Trans } from '@lingui/react/macro';
import { useInfiniteQuery } from '@tanstack/react-query';
import { dispenserQueries } from '@/features/dispensers/queries.ts';
import { EventsTimeline } from '@/features/events/components/events-timeline/EventsTimeline.tsx';
import { mapTimelineDetailItemToTimelineItem } from '@/features/dispensers/mappers/mapTimelineDetailItemToTimelineItem.ts';
import { useTheme } from '@mui/material/styles';
import { EventsTimelineQueryStatus } from '@/features/events/components/events-timeline-query-status/EventsTimelineQueryStatus.tsx';
import { useDeferredValue } from 'react';

export interface EventsTimelineGroupSidebarProps {
  dispenserId: string;
  eventType: EventType;
  startTime: Date;
  endTime: Date;
}

const TIMELINE_PAGE_SIZE = 10;

export const EventsTimelineGroupSidebar = ({
  dispenserId,
  eventType,
  endTime,
  startTime,
}: EventsTimelineGroupSidebarProps) => {
  const theme = useTheme();
  const {
    data: timelinePages,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isError,
    status,
    refetch,
  } = useInfiniteQuery(
    dispenserQueries.timelineDetailInfinite(dispenserId, {
      eventType,
      startTime,
      endTime,
      limit: TIMELINE_PAGE_SIZE,
    })
  );

  const timelineItems = timelinePages?.pages.flatMap((page) =>
    page.items.map(mapTimelineDetailItemToTimelineItem)
  );
  const eventTypeLabel = getEventTypeLabel(eventType);

  // Optimization for slower devices, rendering the timeline can take some time on slower devices
  // which delays opening the sidebar. This shows loading placeholder first which is quick and the
  // sidebar can open immediately and the rendering of the list is deferred. The second parameter
  // is the initial value when the component is mounted, therefor the sidebar needs to remount before
  // opening. For this reason it should be used with key prop.
  const deferredStatus = useDeferredValue(status, 'pending');

  return (
    <Sidebar
      width={{ xs: '90%', sm: 550, md: 800 }}
      backgroundColor={theme.palette.background.paper}
    >
      <Sidebar.Header>
        <Trans>{eventTypeLabel} Event Group</Trans>
      </Sidebar.Header>
      <Sidebar.Content>
        <EventsTimelineQueryStatus status={deferredStatus} data={timelineItems} onRefetch={refetch}>
          <EventsTimeline
            items={timelineItems ?? []}
            canLoadMore={hasNextPage}
            isLoading={isFetchingNextPage}
            onLoadMore={() => fetchNextPage()}
            loadingError={isError ? <Trans>Failed to load events</Trans> : null}
            loadingErrorDescription={
              isError ? (
                <Trans>There has been an error when loading next page of events.</Trans>
              ) : null
            }
          />
        </EventsTimelineQueryStatus>
      </Sidebar.Content>
    </Sidebar>
  );
};
